import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  AlertTriangle, 
  Mail, 
  Building2, 
  User, 
  Clock, 
  Loader2,
  RefreshCw,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActionItemsPage = () => {
  const [actionItems, setActionItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [sendingReminder, setSendingReminder] = useState(null);

  useEffect(() => {
    fetchActionItems();
  }, []);

  const fetchActionItems = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/action-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setActionItems(response.data.items);
      setStats(response.data.by_type);
    } catch (error) {
      toast.error('Failed to load action items');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const sendReminder = async (item) => {
    const token = localStorage.getItem('token');
    setSendingReminder(item.entity_id);
    try {
      // For company items, use owner_name if available, otherwise entity_name
      const userName = item.owner_name || item.entity_name || item.company_name;
      
      // For low_company_profile, pass company name as action_required for personalized email
      let actionRequired = item.action;
      if (item.type === 'low_company_profile') {
        actionRequired = item.entity_name; // Pass company name for the template
      }
      
      await axios.post(`${API}/admin/send-reminder`, {
        user_id: item.entity_id,
        reminder_type: item.type,
        user_email: item.entity_email,
        user_name: userName,
        action_required: actionRequired
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Reminder sent to ${item.entity_email || item.entity_name}`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send reminder';
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setSendingReminder(null);
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };
    return (
      <Badge className={colors[priority] || 'bg-gray-100 text-gray-800'}>
        {priority?.toUpperCase()}
      </Badge>
    );
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'incomplete_profile':
        return <User className="w-5 h-5 text-orange-500" />;
      case 'low_company_profile':
        return <Building2 className="w-5 h-5 text-yellow-500" />;
      case 'expired_trial':
        return <Clock className="w-5 h-5 text-red-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'incomplete_profile':
        return 'Incomplete User Profile';
      case 'low_company_profile':
        return 'Low Company Profile';
      case 'expired_trial':
        return 'Expired Trial';
      default:
        return type;
    }
  };

  const filteredItems = filter 
    ? actionItems.filter(item => item.type === filter)
    : actionItems;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Action Items</h1>
          <p className="text-gray-600 mt-2">Tasks requiring your intervention</p>
        </div>
        <Button onClick={fetchActionItems} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="cursor-pointer hover:shadow-lg" onClick={() => setFilter('')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Items</p>
                <p className="text-3xl font-bold">{actionItems.length}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer hover:shadow-lg ${filter === 'incomplete_profile' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilter(filter === 'incomplete_profile' ? '' : 'incomplete_profile')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Incomplete Profiles</p>
                <p className="text-3xl font-bold text-orange-600">{stats.incomplete_profile || 0}</p>
              </div>
              <User className="w-10 h-10 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer hover:shadow-lg ${filter === 'low_company_profile' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilter(filter === 'low_company_profile' ? '' : 'low_company_profile')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Low Company Profiles</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.low_company_profile || 0}</p>
              </div>
              <Building2 className="w-10 h-10 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer hover:shadow-lg ${filter === 'expired_trial' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilter(filter === 'expired_trial' ? '' : 'expired_trial')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Expired Trials</p>
                <p className="text-3xl font-bold text-red-600">{stats.expired_trial || 0}</p>
              </div>
              <Clock className="w-10 h-10 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Items List */}
      <Card>
        <CardHeader>
          <CardTitle>Action Items {filter && `(${getTypeLabel(filter)})`}</CardTitle>
          <CardDescription>
            {filter && (
              <Button variant="link" className="p-0 h-auto" onClick={() => setFilter('')}>
                Clear filter
              </Button>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <p className="text-gray-500">No action items requiring attention</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredItems.map((item, index) => (
                <div 
                  key={index} 
                  className={`border rounded-lg p-4 ${
                    item.priority === 'high' ? 'border-red-200 bg-red-50' : 
                    item.priority === 'medium' ? 'border-yellow-200 bg-yellow-50' : 
                    'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        {getTypeIcon(item.type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {getPriorityBadge(item.priority)}
                          <Badge variant="outline">{getTypeLabel(item.type)}</Badge>
                        </div>
                        <p className="font-medium text-gray-900">
                          {item.entity_type === 'user' ? item.entity_email : item.entity_name}
                        </p>
                        {item.company_name && item.company_name !== 'N/A' && (
                          <p className="text-sm text-gray-500">Company: {item.company_name}</p>
                        )}
                        {item.profile_completion !== undefined && (
                          <p className="text-sm text-gray-500">Profile completion: {item.profile_completion}%</p>
                        )}
                        <p className="text-sm text-primary mt-1 font-medium">
                          → {item.action}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => sendReminder(item)}
                        disabled={sendingReminder === item.entity_id}
                      >
                        {sendingReminder === item.entity_id ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4 mr-1" />
                        )}
                        {sendingReminder === item.entity_id ? 'Sending...' : 'Send Reminder'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActionItemsPage;
